/**
 * Tipos de domínio — GeoGuard UPM
 * --------------------------------
 * Esta camada é TypeScript PURO. Proibido importar React, Supabase
 * ou qualquer dependência de runtime. O domínio sobrevive a qualquer
 * pivô de stack (Vite → Next, Supabase → NestJS, etc.).
 *
 * Regra: se um tipo aqui muda, é porque o NEGÓCIO mudou — não a tech.
 */

// ── Identificadores opacos (brand types) ──────────────────────
declare const __brand: unique symbol;
type Brand<T, B extends string> = T & { readonly [__brand]: B };

export type DamId = Brand<string, 'DamId'>;
export type InstrumentId = Brand<string, 'InstrumentId'>;
export type ReadingId = Brand<string, 'ReadingId'>;
export type TarpRecordId = Brand<string, 'TarpRecordId'>;
export type EngineerId = Brand<string, 'EngineerId'>;
export type AnomaliaId = Brand<string, 'AnomaliaId'>;
export type BannerId = Brand<string, 'BannerId'>;
export type RecomendacaoId = Brand<string, 'RecomendacaoId'>;

// Helpers de cast (UI usa para passar strings do banco como IDs tipados).
export const asDamId         = (s: string) => s as DamId;
export const asInstrumentId  = (s: string) => s as InstrumentId;
export const asReadingId     = (s: string) => s as ReadingId;
export const asTarpRecordId  = (s: string) => s as TarpRecordId;
export const asEngineerId    = (s: string) => s as EngineerId;
export const asAnomaliaId    = (s: string) => s as AnomaliaId;
export const asBannerId      = (s: string) => s as BannerId;
export const asRecomendacaoId = (s: string) => s as RecomendacaoId;

// ── Enums espelhando o schema ────────────────────────────────
export type DataClass = 'restrito' | 'sensivel' | 'operacional';
export type TarpLevel = 'NORMAL' | 'ATENCAO' | 'ALERTA' | 'EMERGENCIA';
// GUPM-169 — estados v5.10. Helpers e fonte canônica em `./tarp/status.ts` (TarpRecordStatus).
export type TarpStatus = 'ABERTA' | 'EM_ANALISE_RT' | 'CONCLUIDA_AGUARDANDO' | 'ENCERRADA';
// Alinhado com `tipoLeitura` da API GPX/TetraTech (PZ/INA/RL/MV/PLV).
// MS/MSE/TILT não vêm da GPX (topografia/GNSS). Ver src/domain/instrumentos/tipos-leitura.ts.
// EM (estação meteorológica) e INC (inclinômetro) vêm da v2 corporativa (GUPM-127).
export type InstrumentType = 'MS' | 'MSE' | 'INA' | 'PZ' | 'TILT' | 'PLV' | 'MV' | 'RL' | 'EM' | 'INC';
export type ModoLeitura = 'automatico' | 'manual';
export type UserProfile = 'operador' | 'tecnico_responsavel';
export type ReadingOrigem = 'manual' | 'saad' | 'tetratech' | 'tcal' | 'seed' | 'simulacao';
export type ViaAcionamento = 'via1' | 'via2' | 'via3';
export type AnomaliaCategoria = 'ruptura_iminente' | 'anomalia_grave' | 'monitoramento';
export type BannerSeveridade = 'info' | 'aviso' | 'alerta' | 'critico';
export type RecomendacaoOrigem = 'risr' | 'rec_trans' | 'eor_walm' | 'rpsb' | 'oficio_anm' | 'inspecao_campo';
export type RecomendacaoStatus = 'aberta' | 'em_andamento' | 'concluida' | 'cancelada';

// Níveis de emergência ANM (NE-1/NE-2/NE-3 — Res. 95/2022 + 220/2025).
// Independente do TarpLevel: TarpLevel é classificação de instrumento;
// NivelEmergencia é declaração formal pelo Coordenador PAEBM.
export type NivelEmergencia = 'normal' | 'NE-1' | 'NE-2' | 'NE-3';

// ── Entidades ────────────────────────────────────────────────
export interface Dam {
  id: DamId;
  /** `MINA` é estrutura, não barragem (GUPM-329) — rótulo via `rotuloDaEstrutura`. */
  code: 'A' | 'B' | 'C' | 'MINA';
  name: string;
  /** `nao_aplicavel`: estrutura fora da ANM Res. 95/2022 (a Mina). */
  classificacaoDpa: 'baixo' | 'medio' | 'alto' | 'nao_aplicavel';
  classificacaoCri: 'baixo' | 'medio' | 'alto' | 'nao_aplicavel';
  anmResolucao: string;
  isActive: boolean;
}

export interface Instrument {
  id: InstrumentId;
  damId: DamId;
  code: string;
  type: InstrumentType;
  label: string;
  easting: number | null; // null = sem coordenada (ex.: instrumento só-API)
  northing: number | null;
  lat: number | null;
  lon: number | null;
  levelNormalMax: number | null;
  levelAtencaoMax: number | null;
  levelAlertaMax: number | null;
  levelEmergenciaMax: number | null;
  thresholdsValidFrom: Date;
  // GUPM-227: `null` = sem carta de controle. A carta vem de
  // `instrument_thresholds`, materializada dos `param_*` do inventário v2 — e
  // instrumento sem os 3 pontos fica SEM carta em vez de ganhar banda vazia
  // (banda toda-nula casa com qualquer leitura e produz falso ALERTA).
  thresholdsSource: string | null;
  /**
   * GUPM-270 — canais em que existe carta VIGENTE para este instrumento.
   *
   * Instrumento multi-canal (o tiltímetro) nunca terá carta em `canal is null`,
   * só nos eixos: sem esta lista, `thresholdsSource` sozinho — que a view deriva
   * da carta escalar — diria "sem faixa" mesmo com as faixas por eixo carregadas.
   *
   * Opcional e com default vazio de propósito: quem não informa cai no lado
   * SEGURO (sem faixa ⇒ neutro), nunca em verde sem lastro.
   */
  canaisComFaixa?: readonly Canal[];
  // GUPM-227: seção e cotas também passaram a vir do inventário v2 (a Carta de
  // Controle saiu do banco). Null é normal — a v2 não preenche pra todos.
  section: string | null;
  cotaTopo: number | null;
  cotaFundo: number | null;
  profundidade: number | null;
  modoLeitura: ModoLeitura | null;
  // Endereçamento da API GPX/TetraTech (area/estrutura + type + code).
  gpxArea: string | null;
  gpxEstrutura: string | null;
  isActive: boolean;
  /**
   * GUPM-305 — o instrumento publicou dentro da janela de frescor do tipo dele?
   *
   * É conta NOSSA, não campo da API: a GPX v2 nunca informa status, e a perda de sinal se
   * manifesta como **ausência** (o instrumento desaparece do retorno, ou o registro chega
   * com o campo omitido). A ingestão deriva de `idade da última leitura > cadência do tipo
   * + tolerância do tipo` (TIL 20 min, demais 45 — GUPM-308) — tolerância ADITIVA, porque
   * o atraso é do transporte e não cresce com a cadência (`janelaFrescorMin`).
   *
   * `null` = status nunca derivado (instrumento recém-cadastrado). Distinto de `false`:
   * "nunca soubemos" não é "sabemos que está mudo".
   */
  isOnline: boolean | null;
  /**
   * GUPM-305 — instante da última leitura CURADA (GUPM-228: não avança quando a
   * curadoria não produz valor classificável, como na estação meteorológica). Âncora do
   * "sem sinal desde HH:mm".
   */
  lastReadingAt: Date | null;
  /**
   * GUPM-307 — declaração do RT: instrumento fora de serviço desde este instante.
   * `null` = em serviço.
   *
   * Enquanto preenchida, o app **não espera leitura nova da API** para este instrumento:
   * não acusa perda de sinal, não publica nível medido, não pede ação e não entra nos 4
   * níveis reais. É DECLARAÇÃO, não inferência — silêncio inferido tem de abrir TARP de
   * perda de sinal (o `TIL-05` do GUPM-303); silêncio declarado, não. Os dois são
   * `isOnline === false`, e é só isto que os separa.
   *
   * **Não confundir com `modoLeitura`**, que vem do inventário do fornecedor e descreve a
   * forma de COLETA: o `TIL-02` da Barragem B é `manual` e publica 111 leituras por dia.
   */
  foraDeServicoDesde: Date | null;
  /** GUPM-307 — motivo da retirada de serviço, exibido junto do selo. */
  foraDeServicoMotivo: string | null;
  dataClass: DataClass;
}

// ── GUPM-125: Cartas de Controle por banda (min/max) + canal ──
/**
 * Canal de medição de uma leitura (e do threshold correspondente).
 *
 * - `null` → nível d'água (INA/PZ): banda monotônica (min = −inf).
 * - `eixo_a|eixo_b|temperatura` → **tiltímetro** (GUPM-270; ver `CANAIS_TILT`).
 * - `easting|northing|vertical` → **marco superficial** MS/MSE: a campanha
 *   topográfica mede as três (GUPM-329; ver `CANAIS_MARCO`).
 * - `x|y|z|resultante` → canais do desenho original do GUPM-125, sem uso hoje.
 *
 * Espelha o `canal_enum` do Postgres, e é escrito à mão: valor novo no banco não
 * chega aqui sozinho. Há uma **segunda cópia** deste union em `src/types/database.ts`
 * — as duas precisam andar juntas.
 */
export type Canal =
  | 'eixo_a' | 'eixo_b' | 'vertical'
  | 'easting' | 'northing'
  | 'x' | 'y' | 'z' | 'resultante' | 'temperatura';

/** Intervalo de uma banda de classificação. `null` = ilimitado daquele lado. */
export interface ThresholdBand {
  min: number | null;
  max: number | null;
}

/**
 * Carta de Controle de um instrumento (fonte única — GUPM-125).
 * Uma linha por `(instrumento, canal)`. Cada nível é a banda em que ele vale;
 * a classificação escolhe a banda mais interna (menos severa) que contém o
 * valor — o que serve nível d'água (monotônico) e deslocamento (bilateral
 * por eixo) com o mesmo algoritmo. Espelhada 1:1 em `classify_reading_level`
 * no SQL.
 */
export interface InstrumentThreshold {
  instrumentId: InstrumentId;
  canal: Canal | null;
  validFrom: Date;
  normal: ThresholdBand;
  atencao: ThresholdBand;
  alerta: ThresholdBand;
  emergencia: ThresholdBand;
}

export interface Reading {
  id: ReadingId;
  instrumentId: InstrumentId;
  damId: DamId;
  value: number;
  level: TarpLevel | null;          // null = fora de vigência
  observerId: EngineerId | null;
  ts: Date;
  origem: ReadingOrigem;
  notes: string | null;
  dataClass: DataClass;
  /**
   * GUPM-270 — grandeza medida. `null` = canal único (nível d'água, chuva, vazão).
   *
   * A coluna existe em `readings` desde o GUPM-125, mas o mapper do datasource a
   * descartava: o app inteiro era cego a canal, e por isso o tiltímetro não tinha
   * como exibir dois eixos nem escolher qual canal manda no semáforo.
   */
  canal: Canal | null;
}

export interface NewReadingInput {
  instrumentId: InstrumentId;
  value: number;
  ts?: Date;
  origem?: ReadingOrigem;
  notes?: string;
}

export interface AnomaliaCatalogo {
  id: AnomaliaId;
  codigo: string;
  categoria: AnomaliaCategoria;
  titulo: string;
  descricao: string;
  criterioCerteza: string;
  isCertezaIncontestavel: boolean;  // false → M3 Via2 redireciona pra Via3
  exigeCapturaCamera: boolean;
  imagemReferenciaUrl: string | null;
  isActive: boolean;
}

export interface BannerPersistente {
  id: BannerId;
  codigo: string;
  severidade: BannerSeveridade;
  titulo: string;
  descricao: string;
  moduloAlvo: string[];             // ['M1','M3',…]
  dismissableByProfile: UserProfile | null;  // null = não-dispensável
  isActive: boolean;
  damId: DamId | null;
  origemRequisito: string | null;   // 'R-B02', 'PEND-04', etc.
}

export interface Recomendacao {
  id: RecomendacaoId;
  codigo: string;
  origem: RecomendacaoOrigem;
  titulo: string;
  descricao: string;
  status: RecomendacaoStatus;
  prazo: Date | null;
  responsavelEngineerId: EngineerId | null;
  planoAcao: string | null;
  damId: DamId | null;
}
